import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Check, X, Eye } from 'lucide-react';

type KYCDocument = {
  id: string;
  user_id: string;
  doc_type: string;
  file_url: string;
  status: string;
  admin_note: string | null;
  profiles: {
    email: string | null;
    phone: string | null;
  };
};

export default function KYCReview() {
  const { t } = useLanguage();
  const [documents, setDocuments] = useState<KYCDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<KYCDocument | null>(null);
  const [adminNote, setAdminNote] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewImage, setViewImage] = useState<string | null>(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('kyc_documents')
        .select('*, profiles(email, phone)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (docId: string, userId: string, status: 'approved' | 'rejected') => {
    try {
      const { error: docError } = await supabase
        .from('kyc_documents')
        .update({ 
          status, 
          admin_note: adminNote || null 
        })
        .eq('id', docId);

      if (docError) throw docError;

      // Check if all documents are approved
      const { data: userDocs, error: checkError } = await supabase
        .from('kyc_documents')
        .select('status')
        .eq('user_id', userId);

      if (checkError) throw checkError;

      const allApproved = userDocs?.every(doc => doc.status === 'approved');
      const anyRejected = userDocs?.some(doc => doc.status === 'rejected');

      let kycStatus: 'approved' | 'rejected' | 'pending' = 'pending';
      if (allApproved) {
        kycStatus = 'approved';
      } else if (anyRejected) {
        kycStatus = 'rejected';
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ kyc_status: kycStatus })
        .eq('id', userId);

      if (profileError) throw profileError;

      toast.success(`Document ${status}!`);
      setDialogOpen(false);
      setAdminNote('');
      fetchDocuments();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500';
      case 'rejected':
        return 'bg-red-500';
      case 'pending':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('kycReview')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1 flex-1">
                  <div className="font-medium">
                    {doc.profiles?.email || doc.profiles?.phone}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {t(doc.doc_type.replace('_', ''))}
                  </div>
                  <Badge className={getStatusColor(doc.status)}>
                    {t(doc.status)}
                  </Badge>
                  {doc.admin_note && (
                    <p className="text-sm text-muted-foreground italic">{doc.admin_note}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setViewImage(doc.file_url)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Button>
                  {doc.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600"
                        onClick={() => {
                          setSelectedDoc(doc);
                          setDialogOpen(true);
                        }}
                      >
                        <Check className="h-4 w-4 mr-2" />
                        {t('approve')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => {
                          setSelectedDoc(doc);
                          setDialogOpen(true);
                        }}
                      >
                        <X className="h-4 w-4 mr-2" />
                        {t('reject')}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Admin Note (Optional)</Label>
              <Textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Add a note for the user..."
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => selectedDoc && handleUpdateStatus(selectedDoc.id, selectedDoc.user_id, 'approved')}
                className="flex-1"
              >
                <Check className="h-4 w-4 mr-2" />
                {t('approve')}
              </Button>
              <Button
                onClick={() => selectedDoc && handleUpdateStatus(selectedDoc.id, selectedDoc.user_id, 'rejected')}
                variant="destructive"
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                {t('reject')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewImage} onOpenChange={() => setViewImage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Document Preview</DialogTitle>
          </DialogHeader>
          {viewImage && (
            <img src={viewImage} alt="KYC Document" className="w-full h-auto rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}